import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3,
  PieChart,
  TrendingUp,
  Download,
  Filter,
  Calendar,
  Users,
  Target,
  Award,
  FileText,
  Eye,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FeedbackRelatorios = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '30',
    department: '',
    feedbackType: '',
    employee: ''
  });
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    loadReports();
    loadEmployees();
    loadDepartments();
  }, [filters]);

  const loadReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API}/api/feedback/reports?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      // Mock data para desenvolvimento
      setReports({
        summary: {
          totalFeedbacks: 156,
          averageRating: 4.2,
          responseRate: 87,
          completionRate: 92
        },
        byType: {
          continuo: 89,
          performance: 34,
          cultural: 23,
          '360': 10
        },
        byDepartment: {
          'Vendas': 45,
          'Marketing': 32,
          'TI': 28,
          'RH': 25,
          'Financeiro': 26
        },
        trends: [
          { month: 'Jan', feedbacks: 12, rating: 4.1 },
          { month: 'Fev', feedbacks: 18, rating: 4.0 },
          { month: 'Mar', feedbacks: 25, rating: 4.3 },
          { month: 'Abr', feedbacks: 22, rating: 4.2 },
          { month: 'Mai', feedbacks: 31, rating: 4.4 },
          { month: 'Jun', feedbacks: 28, rating: 4.2 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/departamentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      // Mock data
      setDepartments([
        { id: '1', nome: 'Vendas' },
        { id: '2', nome: 'Marketing' },
        { id: '3', nome: 'TI' },
        { id: '4', nome: 'RH' },
        { id: '5', nome: 'Financeiro' }
      ]);
    }
  };

  const handleExportReport = async (format = 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({...filters, format}).toString();
      const response = await axios.get(`${API}/api/feedback/reports/export?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback-report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      alert('Erro ao exportar relatório');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatórios de Feedback</h1>
          <p className="text-gray-600 mt-1">Análises e insights sobre feedback organizacional</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => loadReports()}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Atualizar</span>
          </button>
          
          <button 
            onClick={() => handleExportReport('pdf')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Exportar PDF</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Período
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 3 meses</option>
              <option value="365">Último ano</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Departamento
            </label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({...filters, department: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os departamentos</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Feedback
            </label>
            <select
              value={filters.feedbackType}
              onChange={(e) => setFilters({...filters, feedbackType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os tipos</option>
              <option value="continuo">Contínuo</option>
              <option value="performance">Performance</option>
              <option value="cultural">Cultural</option>
              <option value="360">360°</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Funcionário
            </label>
            <select
              value={filters.employee}
              onChange={(e) => setFilters({...filters, employee: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os funcionários</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.nome_completo}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Resumo Executivo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-3 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Feedbacks</p>
              <p className="text-2xl font-bold text-gray-900">{reports.summary?.totalFeedbacks || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-3 rounded-full">
              <Award className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Avaliação Média</p>
              <p className="text-2xl font-bold text-gray-900">{reports.summary?.averageRating || 0}/5</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-3 rounded-full">
              <Target className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Taxa de Resposta</p>
              <p className="text-2xl font-bold text-gray-900">{reports.summary?.responseRate || 0}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-3 rounded-full">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Taxa de Conclusão</p>
              <p className="text-2xl font-bold text-gray-900">{reports.summary?.completionRate || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feedback por Tipo */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Feedback por Tipo</h3>
            <PieChart className="w-5 h-5 text-gray-500" />
          </div>
          
          <div className="space-y-3">
            {reports.byType && Object.entries(reports.byType).map(([type, count]) => {
              const total = Object.values(reports.byType).reduce((sum, val) => sum + val, 0);
              const percentage = ((count / total) * 100).toFixed(1);
              
              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      type === 'continuo' ? 'bg-blue-500' :
                      type === 'performance' ? 'bg-green-500' :
                      type === 'cultural' ? 'bg-purple-500' :
                      'bg-yellow-500'
                    }`}></div>
                    <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-gray-900">{count}</span>
                    <span className="text-xs text-gray-500 ml-1">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feedback por Departamento */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Feedback por Departamento</h3>
            <BarChart3 className="w-5 h-5 text-gray-500" />
          </div>
          
          <div className="space-y-3">
            {reports.byDepartment && Object.entries(reports.byDepartment).map(([dept, count]) => {
              const maxCount = Math.max(...Object.values(reports.byDepartment));
              const width = (count / maxCount) * 100;
              
              return (
                <div key={dept} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{dept}</span>
                    <span className="text-gray-900">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${width}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tendências */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tendências Mensais</h3>
          <TrendingUp className="w-5 h-5 text-gray-500" />
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Mês</th>
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Feedbacks</th>
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Avaliação Média</th>
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Tendência</th>
              </tr>
            </thead>
            <tbody>
              {reports.trends && reports.trends.map((trend, index) => {
                const prevRating = index > 0 ? reports.trends[index - 1].rating : trend.rating;
                const isUp = trend.rating > prevRating;
                const isDown = trend.rating < prevRating;
                
                return (
                  <tr key={trend.month} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{trend.month}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{trend.feedbacks}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{trend.rating}/5</td>
                    <td className="py-3 px-4">
                      {isUp && <span className="text-green-600 text-sm">↗ Subindo</span>}
                      {isDown && <span className="text-red-600 text-sm">↘ Descendo</span>}
                      {!isUp && !isDown && <span className="text-gray-500 text-sm">→ Estável</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => handleExportReport('excel')}
            className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Exportar Excel</span>
          </button>
          
          <button 
            onClick={() => handleExportReport('csv')}
            className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span>Exportar CSV</span>
          </button>
          
          <button 
            className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>Relatório Detalhado</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackRelatorios;